import React, { useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  CheckBox,
  Heading,
  Field,
  Grid,
  InputGroup,
  InputGroupAddon,
  Spacer,
  Text,
} from '@oliasoft-open-source/react-ui-library';
import { getSections, getOperations, operationsCleared } from 'store/entities/activity-model/activity-model';
import { getElements as getCostElements } from 'store/entities/cost-setup/cost-setup';
import { addRisk, initialRisk } from 'store/entities/risks/risks';
import { getResolver } from 'src/validation/resolver';
import translations from 'src/internationalisation/translation-map.json';
import { risksSchemaValidator } from 'schemas/ajv-validators';
import { Input, CheckBoxList, NumberInput, RadioButton, ControlledText } from 'client/common/form-inputs';
import { useAutoSave } from 'src/common/auto-save/use-auto-save';
import { debounce } from 'lodash';
import { selectSectionOperations } from 'store/entities/activity-model/selectors';
import { selectCostItemsGroupedByTopCategory, selectCostItems } from 'store/entities/cost-setup/selector';
import { autoSaveWaitShort } from 'src/config/config';
import { EffectType, RiskType } from 'src/enums/project-modifiers';
import { withErrorBoundary } from '@oliasoft-open-source/react-ui-library';
import { convertToKebabCase } from 'client/common/utils/string-utils.ts';

const RiskForm = ({
  risk,
  addRisk,
  getOperations,
  relatedItemsIds,
  projectId,
  isPageDisabled,
  getSections,
  groupedItems,
  operationsCleared,
  getCostElements,
}) => {
  const { t } = useTranslation();
  useEffect(() => {
    if (projectId) {
      getOperations(projectId);
      getCostElements(projectId);

      if (!groupedItems.length) {
        getSections(projectId);
      }
    }
    return () => operationsCleared();
  }, [projectId]);
  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isDirty },
  } = useForm({
    mode: 'onChange',
    defaultValues: initialRisk,
    resolver: getResolver(risksSchemaValidator),
  });
  useEffect(() => {
    reset(risk || initialRisk);
  }, [risk?.riskId, reset]);

  const debounceAddRisk = useRef(debounce(addRisk, autoSaveWaitShort));
  const onSubmit = handleSubmit((data) => {
    if (!isDirty) return;
    data.projectId = projectId;
    debounceAddRisk.current(data);
  });
  useAutoSave(onSubmit, watch);
  const { name, effectType, type } = watch();
  const increaseLabel = t(
    type === RiskType.TIME ? translations.projectModifiers_timeIncrease : translations.projectModifiers_costIncrease,
  );
  const decreaseLabel = t(
    type === RiskType.TIME ? translations.projectModifiers_timeDecrease : translations.projectModifiers_costDecrease,
  );
  const effectTypeLabel = effectType === EffectType.Increase ? increaseLabel : decreaseLabel;
  return (
    <>
      <Heading top>{name}</Heading>
      <form>
        <InputGroup>
          <Field label="Name">
            <Input
              testId="project-modifiers-input-name"
              name="name"
              control={control}
              errors={errors}
              width="258px"
              disabled={isPageDisabled}
            />
          </Field>
          <Spacer width="var(--padding-sm)" />
          <Field label={t(translations.projectModifiers_effectType)}>
            <RadioButton
              name="effectType"
              control={control}
              inline={true}
              options={[
                {
                  testId: `project-modifiers-radio-button-effect-type-${convertToKebabCase(increaseLabel)}`,
                  label: increaseLabel,
                  value: EffectType.Increase,
                },
                {
                  testId: `project-modifiers-radio-button-effect-type-${convertToKebabCase(decreaseLabel)}`,
                  label: decreaseLabel,
                  value: EffectType.Decrease,
                },
              ]}
              disabled={isPageDisabled}
            />
          </Field>
          <Spacer width="var(--padding-sm)" />
          <Field label={effectTypeLabel}>
            <InputGroup width={150}>
              <NumberInput
                testId={`project-modifiers-input-${convertToKebabCase(effectTypeLabel)}`}
                name="effect"
                control={control}
                errors={errors}
                disabled={isPageDisabled}
              />
              <InputGroupAddon>%</InputGroupAddon>
            </InputGroup>
          </Field>
        </InputGroup>
        <Spacer />
        <Heading>
          {t(type === RiskType.TIME ? translations.risks_applyToOperations : translations.risks_applyToCostItems)}
        </Heading>
        <Spacer height="var(--padding-xs)" />
        <ControlledText
          testId="project-modifiers-button-check-all"
          name="relatedItems"
          control={control}
          disabled={isPageDisabled}
          onClick={(field) => {
            field.onChange(relatedItemsIds);
          }}
          text={t(translations.checkAll)}
        />
        <Spacer width="var(--padding-sm)" />
        <ControlledText
          name="relatedItems"
          control={control}
          disabled={isPageDisabled}
          onClick={(field) => {
            field.onChange([]);
          }}
          text={t(translations.uncheckAll)}
        />
        <Spacer height="var(--padding-xs)" />
        <Grid columns="1fr 1fr 1fr 1fr 1fr">
          {groupedItems.map((group, index) => {
            return (
              <div
                data-testid={`risk-form-section-${convertToKebabCase(group.name)}`}
                key={`${group.sectionId || group.costCategoryId}-${index}`}
              >
                {group.name && (
                  <Controller
                    name="relatedItems"
                    control={control}
                    render={({ field }) => (
                      <CheckBox
                        testId={`project-modifiers-checkbox-${convertToKebabCase(group.name)}`}
                        onChange={(e) => {
                          const sectionIds = group.items.map((item) => item.id);
                          const currentItems = field.value || [];

                          const nextItems = e.target.checked
                            ? [...new Set([...currentItems, ...sectionIds])]
                            : currentItems.filter((id) => !sectionIds.includes(id));
                          field.onChange(nextItems);
                        }}
                        label={<Text bold>{group.name}</Text>}
                        value={group.sectionId}
                        checked={group.items.every((item) => (field.value || []).includes(item.id))}
                        disabled={isPageDisabled}
                      />
                    )}
                  />
                )}

                <div style={{ paddingLeft: '24px' }}>
                  <CheckBoxList
                    testId="project-modifiers"
                    groupName={group.name}
                    items={group.items}
                    control={control}
                    errors={errors}
                    name="relatedItems"
                    disabled={isPageDisabled}
                  />
                </div>
              </div>
            );
          })}
        </Grid>
      </form>
    </>
  );
};

const mapStateToProps = (state, { risk }) => {
  const operationIds = state.entities.activityModel.operations.map((operation) => operation.operationId);
  const costItemIds = selectCostItems(state).map((costItem) => costItem.costItemId);
  const relatedItemsIds = risk && risk?.type === RiskType.COST ? costItemIds : operationIds;
  const groupedItems =
    risk && risk?.type === RiskType.COST ? selectCostItemsGroupedByTopCategory(state) : selectSectionOperations(state);
  return {
    isAdding: state.entities.risks.isAdding,
    relatedItemsIds,
    groupedItems,
  };
};

const mapDispatchToProps = {
  addRisk,
  getOperations,
  operationsCleared,
  getSections,
  getCostElements,
};

const Container = withErrorBoundary(connect(mapStateToProps, mapDispatchToProps)(RiskForm));

export { Container as RiskForm };
